import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../global/default-button/default-button';
import { SidebarFooter } from '../../_sidebar-footer/sidebar-footer';
import { PageSelectorDropdown } from '../_manage-page/page-selector-dropdown/page-selector-dropdown';
import { CANCEL_TEXT, LOADING, LOGO_IMAGE_TEXT } from '../../../../../../constants/common-labels.constants';
import {
    CUSTOM_CSS_APPLIED_TEXT,
    CUSTOM_CSS_LABEL_TEXT,
    CUSTOM_CSS_NOT_APPLIED_TEXT,
    EDIT_CUSTOM_CSS_TEXT,
    HUB_BACKGROUND_IMAGE_INFO_TEXT,
    HUB_LOGO_IMAGE_INFO_TEXT,
    HUB_SETTINGS_SELECT_LABEL_TEXT,
    SAVE_CHANGES_BUTTON_TEXT,
} from '../../../../event-hubs.text';
import { HEX_OR_RGB_TEXT, PRIMARY_COLOR_TEXT } from '../../../../../_events/_manage-event/_edit-website/website-form-body/website-form.texts';
import { IMAGE_BACKGROUND_TEXT } from '../../../../../_events/_manage-event/_edit-website/website-form-body/passport-settings-form/passport-settings-form.texts';
import { DEFAULT_HUB_PRIMARY_COLOR } from '../../../../../../constants/hub-branding.constants';
import { UploadImageS3Presentation } from '../../../../../_events/_manage-event/shared/upload-image/upload-image-to-s3.presentation';
import { EditHubVM } from './edit-hub.viewmodel';
import { EditHubSettingType } from './edit-hub-settings-type';
import { HubRegistrationSettings } from './_registration/hub-registration-settings';
import { HubSignInSettings } from './_sign-in/hub-sign-in-settings';
import { HubFooterSettings } from './_footer/hub-footer-settings';
import { HubMenuSettings } from './_menu/hub-menu-settings';
import { EditCustomCssModal } from './_custom-css/edit-custom-css-modal';
import './edit-hub.scss';

type EditHubPresentationProps = {
    vm: EditHubVM;
};

export const EditHubPresentation: React.FC<EditHubPresentationProps> = observer(({ vm }) => {
    const hubSettingsPrefix = vm.intl.formatMessage(HUB_SETTINGS_SELECT_LABEL_TEXT);
    const settingOptions = vm.getAvailableSettingOptions();
    const selectedSetting = vm.getSelectedSetting();
    const selectedOption = settingOptions.find(option => option.value === selectedSetting);
    const logoImageLabel = vm.intl.formatMessage(LOGO_IMAGE_TEXT);
    const backgroundImageLabel = vm.intl.formatMessage(IMAGE_BACKGROUND_TEXT);

    return (
        <div className='edit-hub'>
            <div className='edit-hub__header'>
                <PageSelectorDropdown
                    className='edit-hub__settings-select'
                    prefix={hubSettingsPrefix}
                    selectedLabel={selectedOption?.label ?? ''}
                    options={settingOptions}
                    selectedValue={selectedSetting}
                    onChange={value => vm.selectSetting(value as EditHubSettingType)}
                />
            </div>
            <div className='edit-hub__body'>
                {selectedSetting === EditHubSettingType.REGISTRATION && vm.registrationVM ? (
                    <HubRegistrationSettings vm={vm.registrationVM} />
                ) : selectedSetting === EditHubSettingType.SIGN_IN && vm.signInVM ? (
                    <HubSignInSettings vm={vm.signInVM} />
                ) : selectedSetting === EditHubSettingType.FOOTER && vm.footerVM ? (
                    <HubFooterSettings vm={vm.footerVM} />
                ) : selectedSetting === EditHubSettingType.MENU && vm.menuVM ? (
                    <HubMenuSettings vm={vm.menuVM} />
                ) : vm.isLoading() ? (
                    <div className='edit-hub__loading'>
                        <FormattedMessage {...LOADING} values={{ suffix: '...' }} />
                    </div>
                ) : (
                    <>
                        <div className='edit-hub__primary-color m-bottom-25px'>
                            <label className='oe-form-field__title'>
                                <FormattedMessage {...PRIMARY_COLOR_TEXT} />
                            </label>
                            <div className='edit-hub__primary-color-field'>
                                <span
                                    className='edit-hub__color-swatch'
                                    style={{ backgroundColor: vm.getNormalizedPrimaryColor() || DEFAULT_HUB_PRIMARY_COLOR }}
                                />
                                <input
                                    className='oe-form-field__control text-blue font-700 edit-hub__primary-color-input'
                                    type='text'
                                    placeholder={vm.intl.formatMessage(HEX_OR_RGB_TEXT)}
                                    value={vm.getPrimaryColor() || ''}
                                    onChange={e => vm.setPrimaryColor(e.target.value)}
                                />
                            </div>
                            {vm.getColorValidationError() && <span className='oe-form-field__error'>{vm.getColorValidationError()}</span>}
                        </div>
                        <div className='edit-hub__logo-image m-bottom-25px'>
                            <UploadImageS3Presentation
                                vm={vm.uploadLogoImageS3VM}
                                fieldName={logoImageLabel}
                                infoIconText={vm.intl.formatMessage(HUB_LOGO_IMAGE_INFO_TEXT)}
                            />
                        </div>
                        <div className='edit-hub__background-image m-bottom-25px'>
                            <UploadImageS3Presentation
                                vm={vm.uploadBackgroundImageS3VM}
                                fieldName={backgroundImageLabel}
                                infoIconText={vm.intl.formatMessage(HUB_BACKGROUND_IMAGE_INFO_TEXT)}
                            />
                        </div>
                        <div className='edit-hub__custom-css m-bottom-25px'>
                            <label className='oe-form-field__title'>
                                <FormattedMessage {...CUSTOM_CSS_LABEL_TEXT} />
                            </label>
                            <div className='edit-hub__custom-css-field'>
                                <span
                                    className={`edit-hub__custom-css-indicator ${
                                        vm.hasCustomCssApplied()
                                            ? 'edit-hub__custom-css-indicator--applied'
                                            : 'edit-hub__custom-css-indicator--not-applied'
                                    }`}
                                >
                                    <FormattedMessage {...(vm.hasCustomCssApplied() ? CUSTOM_CSS_APPLIED_TEXT : CUSTOM_CSS_NOT_APPLIED_TEXT)} />
                                </span>
                                <DefaultButton
                                    text={<FormattedMessage {...EDIT_CUSTOM_CSS_TEXT} />}
                                    onClick={vm.openCustomCssEditor}
                                />
                            </div>
                        </div>
                        {vm.isCustomCssEditorOpen() && <EditCustomCssModal vm={vm} />}
                    </>
                )}
            </div>
            <SidebarFooter>
                <DefaultButton
                    modifier='cancel'
                    text={<FormattedMessage {...CANCEL_TEXT} />}
                    onClick={vm.onCancel}
                    disabled={vm.isCancelButtonDisabled()}
                />
                <DefaultButton
                    text={<FormattedMessage {...SAVE_CHANGES_BUTTON_TEXT} />}
                    onClick={vm.onSaveChanges}
                    disabled={vm.isSaveButtonDisabled()}
                />
            </SidebarFooter>
        </div>
    );
});
